import { Kinesis } from '@/services/TrackerService';
import { ILogFields } from '@/services/TrackerService/Kinesis/interfaces';
import { throttleAndWaitPrev } from '@/utils/functions/eventHandler/throttleAndWaitPrev';
import { prop } from 'ramda';
import React, { forwardRef } from 'react';

export interface IClickTrackerProps<T> {
  children: React.ReactElement;
  /** 페이지명 */
  pageName?: string;
  /** log data */
  logData?: T;
  /** click log 전송 함수 */
  sendLog?: (data: T) => Promise<void>;
}

/**
 * click 클라이언트 로그(Kinesis) 로그를 전송한다.
 *
 * 자식은 `onClick?`을 받을 수 있어야 한다.
 * 이미 `onClick`을 받을 수 있는 공용 컴포넌트나 HTML 요소는 `onClick`을 자동으로 주입받는다.
 * 추가 작업이 필요해 자식에 `onClick`을 따로 추가할 경우, `ClickHandler`의 추적 코드 이행 이후 자식의 `onClick`을 호출한다.
 *
 * @example
 * <ClickTracker {...}>
 *   <SunnyLink href="/">link</SunnyLink>
 * </ClickTracker>
 *
 * <ClickTracker {...}>
 *   <TrackMe />
 * </ClickTracker>
 *
 * // ...
 *
 * const TrackMe: React.FC<{ onClick?: React.MouseEventHandler }> = ({ onClick }) => (
 *   <section>
 *     <button onClick={onClick} />
 *   </section>
 * )
 */
const ClickTrackerInner = <T extends Partial<ILogFields>, P>(
  { children, pageName, logData, sendLog }: IClickTrackerProps<T>,
  ref: React.ForwardedRef<P>,
): React.ReactElement => {
  const originalClickListener = children.props.onClick;
  const trackClick = (): Promise<void> => {
    if (!logData) {
      return Promise.resolve();
    }
    if (sendLog) {
      return sendLog(logData);
    }
    const _pageName = pageName ?? prop('pageName', logData) ?? '';
    return Kinesis.put({
      ...logData,
      eventType: 'click',
      pageName: _pageName,
    });
  };

  return React.cloneElement(children, { onClick: throttleAndWaitPrev(originalClickListener, trackClick), ref });
};

const ClickTracker = forwardRef(ClickTrackerInner) as <T extends Partial<ILogFields>, P>(
  props: IClickTrackerProps<T> & { ref?: React.Ref<P> },
) => React.ReactElement;

export default ClickTracker;
